import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { createPortal } from 'react-dom';
import { TabularText } from '@/components/TabularText';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  importsApi,
  type ImportBatch,
  type ImportBatchRow,
  type ImportRowOutcome,
} from '@/api/imports';
import { errorCopy } from '@/api/errorCopy';
import { SegmentedControl } from '@/components/SegmentedControl';
import { ErrorState } from '@/components/ErrorState';
import { useConfirm } from '@/components/useConfirm';
import { DiscardPanel } from './DiscardPanel';
import { useScrollsX } from '@/shared/useScrollsX';
import { resolveTokens } from '@/theme/theme';
import { unallocatedHref } from '@/features/accounts/unallocatedHref';
import { beforeOpeningLabels } from './beforeOpeningLabels';

const OUTCOMES: ImportRowOutcome[] = ['IMPORTED', 'DUPLICATE', 'REJECTED', 'BEFORE_OPENING'];

interface ReviewStepProps {
  accountId: number;
  batch: ImportBatch;
  readOnly: boolean;
  /** From commit response when the body carries `historical`. */
  historicalCount?: number | null;
  /** The account's budget start: Open register shows from it (TMMAPP-0117 A1). */
  budgetStartOn?: string | null;
  /** The committed file's earliest line, when known: Open register starts there if that's earlier. */
  firstLineDate?: string | null;
  onOpenTransaction: (transactionId: number) => void;
  onDiscarded: () => void;
  /** The modal footer's right-hand slot: Done renders there (null while it mounts). */
  actionsHost?: HTMLElement | null;
  /** Done; defaults to going back to the account page. */
  onDone?: () => void;
  /** The modal's panel for the discard confirmation (`undefined` outside the modal; DLG-005). */
  panelHost?: HTMLElement | null;
  /** Reports the discard confirmation showing, so the modal hides its own content. */
  onConfirming?: (confirming: boolean) => void;
}

export function ReviewStep({
  accountId,
  batch,
  readOnly,
  historicalCount = null,
  budgetStartOn = null,
  firstLineDate = null,
  onOpenTransaction,
  onDiscarded,
  actionsHost,
  onDone,
  panelHost,
  onConfirming,
}: ReviewStepProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const [outcomeTrack, setOutcomeTrack] = useState<HTMLDivElement | null>(null);
  const outcomesScroll = useScrollsX(outcomeTrack);
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState<ImportRowOutcome | undefined>(undefined);
  const [rows, setRows] = useState<ImportBatchRow[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null);
  const discarding = useConfirm<true>();

  const load = useCallback(async () => {
    try {
      const page = await importsApi.rows(accountId, batch.id, {
        ...(outcome !== undefined ? { outcome } : {}),
        limit: 50,
      });
      setRows(page.items);
      setLoadFailure(null);
    } catch (error) {
      setLoadFailure({ error });
    }
  }, [accountId, batch.id, outcome]);

  useEffect(() => {
    let cancelled = false;
    void importsApi
      .rows(accountId, batch.id, {
        ...(outcome !== undefined ? { outcome } : {}),
        limit: 50,
      })
      .then((page) => {
        if (cancelled) return;
        setRows(page.items);
        setLoadFailure(null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setLoadFailure({ error });
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, batch.id, outcome]);

  const tiles = [
    { key: 'imported', value: batch.rowsImported },
    { key: 'duplicate', value: batch.rowsDuplicate },
    { key: 'rejected', value: batch.rowsRejected },
    { key: 'confirmMatch', value: batch.confirmMatch },
    { key: 'replaceSynthetic', value: batch.replaceSynthetic },
    { key: 'planPartial', value: batch.planPartial },
  ] as const;
  const beforeOpening = beforeOpeningLabels(t, batch);
  const done = onDone ?? (() => void navigate(`/me/accounts/${accountId}`));
  // TMMAPP-0141 §B: a successful import ends with three choices (the setup wizard is gone). They
  // stack at `xs`, Review payees on top.
  const doneButton =
    batch.status === 'COMMITTED' ? (
      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        sx={{ gap: 1, alignItems: { xs: 'stretch', sm: 'center' } }}
        data-review-actions=""
      >
        <Button onClick={done}>{t('imports.done')}</Button>
        <Button
          variant="outlined"
          component={RouterLink}
          to={unallocatedHref(
            accountId,
            budgetStartOn,
            firstLineDate,
            // Lines before the opening date that this import brought in are its earliest.
            batch.includeBeforeOpening ? batch.beforeOpeningFrom : null,
          )}
        >
          {t('imports.openRegister')}
        </Button>
        <Button variant="contained" component={RouterLink} to="/me/payees">
          {t('imports.reviewPayees')}
        </Button>
      </Stack>
    ) : (
      <Button variant="contained" onClick={done}>
        {t('imports.done')}
      </Button>
    );

  return (
    <Stack spacing={2}>
      {historicalCount !== null && historicalCount > 0 && (
        <Alert severity="info">{t('imports.historicalCount', { count: historicalCount })}</Alert>
      )}
      <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 1.5, flexWrap: 'wrap' }}>
        {tiles.map((tile) => (
          // The prototype's review stat boxes (P:1790): 1px `line`, `overline` label, `stat` value.
          <Box
            key={tile.key}
            data-stat-box=""
            sx={{
              p: 1.5,
              minWidth: 120,
              border: `1px solid ${tokens.line}`,
              borderRadius: `${tokens.radius.control}px`,
            }}
          >
            <Typography variant="overline" component="div">
              {t(`imports.tile.${tile.key}`)}
            </Typography>
            <Typography variant="stat" component="div">
              {tile.value}
            </Typography>
          </Box>
        ))}
      </Stack>
      {beforeOpening.map((line) => (
        <Typography key={line} variant="body2" sx={{ color: 'text.secondary' }}>
          {line}
        </Typography>
      ))}
      {/* Outcome labels do not wrap. Keep the track in this box so a phone page does not scroll
          sideways. It is a named region with a tab stop only while it scrolls (B14): otherwise the
          group inside is the one "Row outcomes". */}
      <Box
        ref={setOutcomeTrack}
        {...(outcomesScroll
          ? { role: 'region', 'aria-label': t('imports.outcomeFilter'), tabIndex: 0 }
          : {})}
        sx={{
          minWidth: 0,
          width: '100%',
          overflowX: 'auto',
          '&:focus-visible': {
            outline: `2px solid ${tokens.focusRing}`,
            outlineOffset: '2px',
          },
        }}
      >
        <SegmentedControl
          aria-label={t('imports.outcomeFilter')}
          value={outcome ?? 'ALL'}
          options={[
            { value: 'ALL' as const, label: t('imports.outcomeAll') },
            ...OUTCOMES.map((value) => ({ value, label: t(`imports.outcome.${value}`) })),
          ]}
          onChange={(next) => setOutcome(next === 'ALL' ? undefined : next)}
        />
      </Box>
      {loadFailure !== null && (
        <ErrorState
          variant="section"
          error={loadFailure.error}
          what={t('errors.what.importRows')}
          onRetry={() => void load()}
        />
      )}
      <Table size="small" aria-label={t('imports.stepReview')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('imports.colRow')}</TableCell>
            <TableCell>{t('imports.colStatus')}</TableCell>
            <TableCell>{t('imports.colRaw')}</TableCell>
            <TableCell>{t('imports.colReason')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const key = row.rowNumber;
            const long = row.rawLine.length > 80;
            const shown = expanded === key || !long ? row.rawLine : `${row.rawLine.slice(0, 80)}…`;
            return (
              <TableRow key={key}>
                <TableCell>
                  <TabularText>{row.rowNumber}</TabularText>
                </TableCell>
                <TableCell>
                  {row.outcome !== null ? t(`imports.outcome.${row.outcome}`) : '—'}
                </TableCell>
                <TableCell>
                  <Typography variant="mono">{shown}</Typography>
                  {long && (
                    <Button size="small" onClick={() => setExpanded(expanded === key ? null : key)}>
                      {t('imports.expandLine')}
                    </Button>
                  )}
                  <Collapse in={expanded === key}>
                    <Typography variant="mono">{row.rawLine}</Typography>
                  </Collapse>
                  {row.transactionId !== null && (
                    <Button size="small" onClick={() => onOpenTransaction(row.transactionId!)}>
                      {t('review.open')}
                    </Button>
                  )}
                  {row.duplicateOfTransactionId !== null && (
                    <Button
                      size="small"
                      onClick={() => onOpenTransaction(row.duplicateOfTransactionId!)}
                    >
                      {t('review.open')}
                    </Button>
                  )}
                </TableCell>
                <TableCell>{row.rejectReason !== null ? t('errors.rowUnreadable') : '—'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {actionsHost !== undefined && actionsHost !== null && createPortal(doneButton, actionsHost)}
      <Box>
        {actionsHost === undefined && doneButton}
        {!readOnly && (
          <Button color="error" sx={{ marginInlineStart: 1 }} onClick={() => discarding.ask(true)}>
            {t('imports.discard')}
          </Button>
        )}
      </Box>
      <DiscardPanel
        open={discarding.target !== null}
        panelHost={panelHost}
        onConfirming={onConfirming}
        title={t('imports.discardTitle', { file: batch.originalFilename })}
        consequence={t('imports.discardConsequence')}
        confirmLabel={t('imports.discardConfirm')}
        destructive
        busy={discarding.busy}
        error={discarding.error}
        onConfirm={() =>
          void discarding.run(
            async () => {
              await importsApi.discard(accountId, batch.id);
              onDiscarded();
            },
            (error) => errorCopy(t, error, 'accounts.errorGeneric'),
          )
        }
        onCancel={discarding.cancel}
      />
    </Stack>
  );
}
